/**
 * Member Activation builder — design model (AR-328).
 *
 * Design/layout ticket: the module/step tree, the step-type catalogue and the
 * KPI/archive figures below are the fixture the admin editor is designed
 * against. The shape mirrors the downstream module/step APIs so the views can
 * be repointed at the server without touching the layout.
 */

/* ── Routes, pathways, plans ─────────────────────────────────────────────── */

export type ActivationRouteCode = 'san' | 'sae' | 'mae' | 'aa';

export interface ActivationRoute {
  code: ActivationRouteCode;
  /** Compact chip label used in the module/step list. */
  short: string;
  label: string;
}

export const ACTIVATION_ROUTES: ActivationRoute[] = [
  { code: 'san', short: 'N1', label: 'New Single-Agent Agency' },
  { code: 'sae', short: 'E1', label: 'Experienced Single-Agent Agency' },
  { code: 'mae', short: 'EM', label: 'Experienced Multi-Agent Agency' },
  { code: 'aa', short: 'AA', label: 'Associate Advisor' },
];

export const ALL_ROUTES: ActivationRouteCode[] = ['san', 'sae', 'mae', 'aa'];
/** Every route except Associate Advisor — the agency-owner routes. */
export const AGENCY_ROUTES: ActivationRouteCode[] = ['san', 'sae', 'mae'];

export const ACTIVATION_PATHWAYS = [
  'Sea + Sand',
  'World Explorer',
  'Theme Parks',
  'Corporate Travel',
] as const;
export type ActivationPathway = (typeof ACTIVATION_PATHWAYS)[number];

export const ACTIVATION_PLANS = ['Individual', 'Growth', 'Team', 'Enterprise'] as const;
export type ActivationPlan = (typeof ACTIVATION_PLANS)[number];

export const routeShort = (code: ActivationRouteCode): string =>
  ACTIVATION_ROUTES.find((r) => r.code === code)?.short ?? code;

export const routeLabel = (code: ActivationRouteCode): string =>
  ACTIVATION_ROUTES.find((r) => r.code === code)?.label ?? code;

/* ── Step types ──────────────────────────────────────────────────────────── */

export type ActivationStepType = 'Simple' | 'Form' | 'Form — AI' | 'Add-On' | 'System';
export type ActivationRequirement = 'required' | 'skippable' | 'optional';

export interface StepTypeStyle {
  /** Colour of the leading dot on the type badge. */
  dot: string;
  /** Tint used for the type's icon tiles and menu bullets. */
  tile: string;
}

/**
 * Type colours are shared by the list badges, the add-step menu and the
 * content-block tiles, so a step type reads the same everywhere.
 */
export const STEP_TYPE_STYLES: Record<ActivationStepType, StepTypeStyle> = {
  Simple: { dot: 'bg-orange-500', tile: 'bg-orange-500' },
  Form: { dot: 'bg-teal-600', tile: 'bg-teal-600' },
  'Form — AI': { dot: 'bg-teal-600', tile: 'bg-teal-600' },
  'Add-On': { dot: 'bg-pink-600', tile: 'bg-pink-600' },
  System: { dot: 'bg-violet-500', tile: 'bg-violet-500' },
};

export const REQUIREMENT_LABELS: Record<ActivationRequirement, string> = {
  required: 'Required',
  skippable: 'Skippable',
  optional: 'Optional',
};

export const REQUIREMENT_BADGE_CLASSES: Record<ActivationRequirement, string> = {
  required:
    'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-900',
  skippable:
    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
  optional:
    'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
};

/** The four types an admin may add. `System` steps are engineering-owned. */
export interface StepTypeOption {
  type: Exclude<ActivationStepType, 'System'>;
  name: string;
  description: string;
}

export const ADD_STEP_TYPES: StepTypeOption[] = [
  {
    type: 'Simple',
    name: 'Simple Step',
    description: 'Video, rich text, file attachment, and a CTA button.',
  },
  {
    type: 'Form',
    name: 'Form Answer — Standard',
    description: 'Input fields mapped to member profile fields.',
  },
  {
    type: 'Form — AI',
    name: 'Form Answer — AI-Generative',
    description: 'Member seeds + AI draft saved to a profile field.',
  },
  {
    type: 'Add-On',
    name: 'Add-On',
    description: 'Purchasable product tied to a Stripe price.',
  },
];

/* ── Module / step model ─────────────────────────────────────────────────── */

export interface ActivationFormField {
  label: string;
  /** Profile field the answer is written to, e.g. `{{primary_goal}}`. */
  map: string;
  required: boolean;
}

export interface ActivationEmail {
  subject: string;
  to: string;
  trigger: string;
}

export interface ActivationStep {
  /** Opaque stable id for selection and drag; seeded as `moduleId:index`. */
  key: string;
  moduleId: string;
  moduleName: string;
  name: string;
  type: ActivationStepType;
  requirement: ActivationRequirement;
  routes: ActivationRouteCode[];
  /** Present when the step is scoped to specific pathways. */
  pathways?: ActivationPathway[];
  /** Present when the step is scoped to specific plan tiers. */
  plans?: ActivationPlan[];
  /** System steps: read-only, cannot be moved, edited or hidden. */
  locked?: boolean;
  video?: boolean;
  fields?: ActivationFormField[];
  seeds?: string[];
  aiPrompt?: string;
  aiVariables?: string[];
  aiOutput?: string;
  systemFields?: string;
  systemLogic?: string;
  emails?: ActivationEmail[];
  /** Marks the pre-filled shape shown when adding a brand-new step. */
  isExample?: boolean;
}

export interface ActivationModule {
  id: string;
  order: number;
  name: string;
  /** Header label the member sees, e.g. Checkout → "Getting Started". */
  navLabel: string;
  description: string;
  routes: ActivationRouteCode[];
  /** System modules render locked — steps can be read but not rearranged. */
  locked?: boolean;
  /** Module still being built out; steps are placeholders. */
  buildOut?: boolean;
  miniTracker?: boolean;
  archived?: boolean;
  steps: ActivationStep[];
}

type StepSeed = Omit<ActivationStep, 'key' | 'moduleId' | 'moduleName'>;

const step = (
  name: string,
  type: ActivationStepType,
  requirement: ActivationRequirement,
  routes: ActivationRouteCode[],
  rest: Partial<StepSeed> = {},
): StepSeed => ({ name, type, requirement, routes, ...rest });

const MODULE_SEEDS: Array<Omit<ActivationModule, 'steps'> & { steps: StepSeed[] }> = [
  {
    id: 'checkout',
    order: 1,
    name: 'Checkout',
    navLabel: 'Getting Started',
    description: 'Sign-up, payment & account creation.',
    routes: AGENCY_ROUTES,
    locked: true,
    miniTracker: true,
    steps: [
      step('Create Your Login', 'System', 'required', AGENCY_ROUTES, {
        locked: true,
        systemFields:
          'Email · First / Last name → user table.\nHidden: Status = Checkout Pending · Role = Agency Owner · Plan.',
        systemLogic:
          'Provisions user in AIVIA + WorldVia PRO (Auth0 TBD).\nHubSpot: match by email → connect + stamp custom key, or create.\nRuns Grad-list name check (manual at MVP).',
      }),
      step('Contact Information', 'System', 'required', AGENCY_ROUTES, {
        locked: true,
        systemFields:
          'Legal name · billing/mailing (USPS-verified) · phone · agency name · associate count · referral.',
        systemLogic:
          '"Same as billing" autofills mailing. Writes to AIVIA + PRO; auto-creates/associates HubSpot company on brand name.',
      }),
      step('Add the Accelerator Course?', 'Add-On', 'required', AGENCY_ROUTES, {
        video: true,
        emails: [],
      }),
      step('Payment', 'System', 'required', AGENCY_ROUTES, {
        locked: true,
        systemLogic:
          'Stripe payment capture. Plan → Stripe product/price; coupon applied if present.',
      }),
      step('Complete Membership Purchase', 'System', 'required', AGENCY_ROUTES, {
        locked: true,
        systemLogic:
          'Creates Stripe customer + subscription; status → Pending; HubSpot updated; Stripe sends receipt.',
        emails: [
          { subject: 'Your WorldVia payment receipt', to: 'Member', trigger: 'Stripe' },
          { subject: 'Welcome — your next steps', to: 'Member', trigger: 'Purchase complete' },
        ],
      }),
    ],
  },
  {
    id: 'activation',
    order: 2,
    name: 'Activation',
    navLabel: 'Activation',
    description: 'Activate the account & core setup.',
    routes: ALL_ROUTES,
    miniTracker: true,
    steps: [
      step('Welcome to WorldVia', 'Simple', 'required', ALL_ROUTES, { video: true }),
      step('Tell Us About Your Business', 'Form', 'required', AGENCY_ROUTES, {
        emails: [],
        fields: [
          { label: 'Primary 12-month goal', map: '{{primary_goal}}', required: true },
          { label: 'Types of travel', map: '{{travel_types}}', required: true },
          { label: 'Experience level', map: '{{experience_level}}', required: true },
          { label: 'Last-year sales revenue', map: '{{sales_revenue}}', required: false },
          { label: 'Hours per week', map: '{{weekly_hours}}', required: true },
        ],
      }),
      step('Verify Your Identity', 'System', 'required', AGENCY_ROUTES, {
        locked: true,
        systemLogic:
          'Launches Stripe Identity (verify.stripe.com) in a new window; API validates success/failure. Retry path on fail.',
      }),
      step('Background Check Authorization', 'System', 'required', AGENCY_ROUTES, {
        locked: true,
        systemFields: 'Date of birth → profile + HubSpot.',
        systemLogic:
          'Digital signature + disclosure. Prefills agency + name. Generates secured PDF; stored to profile + SharePoint; secure email to member.',
        emails: [
          {
            subject: 'Your background check authorization',
            to: 'Member',
            trigger: 'On signature',
          },
        ],
      }),
      step('Submit Your W-9', 'System', 'required', AGENCY_ROUTES, {
        locked: true,
        plans: ['Individual'],
        systemLogic:
          'Secure upload with downloadable W-9. PDF visible to admins; team alerted; saved to SharePoint.',
        emails: [{ subject: 'W-9 received — team review', to: 'Team', trigger: 'On upload' }],
      }),
      step(
        'Register Your Business — Certificate of Formation',
        'System',
        'skippable',
        AGENCY_ROUTES,
        {
          locked: true,
          video: true,
          systemLogic:
            'Secure upload (Growth+). Skippable with commission-hold acknowledgment. Team alerted; saved to SharePoint.',
        },
      ),
      step('Obtain Your EIN — IRS EIN Letter', 'System', 'skippable', AGENCY_ROUTES, {
        locked: true,
        video: true,
        systemLogic:
          'Secure upload. Skippable with commission-hold acknowledgment. Team alerted; saved to SharePoint.',
      }),
      step('Performance Guaranty & Member Agreement', 'System', 'required', AGENCY_ROUTES, {
        locked: true,
        systemLogic:
          'Digital signature. Generates secured PDF with addendum (timestamp, IP, plan). Stored to profile + SharePoint; member-downloadable copy emailed.',
        emails: [
          { subject: 'Your signed Member Agreement', to: 'Member', trigger: 'On signature' },
        ],
      }),
      step('Banking & Direct Deposit', 'System', 'skippable', AGENCY_ROUTES, {
        locked: true,
        systemLogic:
          'ACH authorization + signature. Secured PDF stored to profile + SharePoint; emailed. Triggers TRAMS update workflow (manual at MVP). Member Success alerted.',
        emails: [{ subject: 'Banking info received', to: 'Team', trigger: 'On save' }],
      }),
      step('Understanding WorldVia', 'Simple', 'required', ALL_ROUTES, { video: true }),
      step('About Your Plan', 'Simple', 'required', AGENCY_ROUTES, { video: true }),
      step('Meet the WorldVia Team', 'Simple', 'required', ALL_ROUTES, { video: true }),
      step('What a Travel Advisor Does', 'Simple', 'required', ['san', 'aa'], { video: true }),
      step('Choose Your Travel Pathway', 'Simple', 'required', ALL_ROUTES, { video: true }),
      step('Define Your Niche', 'Form — AI', 'required', ALL_ROUTES, {
        video: true,
        seeds: ['Keywords', 'Pathway', 'Company name', 'Draft text'],
        aiPrompt:
          'Write a concise, confident niche statement for a travel advisor. Use their keywords, pathway {{pathway}}, and company {{company_name}}. One or two sentences, first person.',
        aiVariables: ['{{pathway}}', '{{company_name}}', '{{experience_level}}', '{{primary_goal}}'],
        aiOutput: '{{niche}}',
      }),
      step('Attend the Newbie Meet + Greet', 'Simple', 'skippable', ALL_ROUTES),
    ],
  },
  {
    id: 'business-foundation',
    order: 3,
    name: 'Business Foundation',
    navLabel: 'Business Foundation',
    description: 'Build the foundation of the business.',
    routes: ALL_ROUTES,
    steps: [
      step('Navigating WorldVia', 'Simple', 'required', ALL_ROUTES, {
        video: true,
        systemLogic:
          'Status Pending → Active; written to AIVIA + PRO, pushed to HubSpot. Member Activation link appears in headers.',
        emails: [
          {
            subject: "You're active! Getting around WorldVia",
            to: 'Member',
            trigger: 'Activation — incl. Commission Manager + Agent Universe logins',
          },
        ],
      }),
      step('Build Your Brand Trifecta', 'Form — AI', 'optional', AGENCY_ROUTES, {
        video: true,
        seeds: ['Company name', 'Tagline draft', 'Pitch draft'],
        aiPrompt:
          'Generate a tagline, elevator pitch, and value proposition for {{company_name}}, referencing niche {{niche}} and pathway {{pathway}}. Keep each on-brand and client-facing.',
        aiVariables: ['{{niche}}', '{{pathway}}', '{{company_name}}', '{{primary_goal}}'],
        aiOutput: '{{tagline}}, {{elevator_pitch}}, {{value_prop}}',
      }),
      step('Set Up Your Public CTAs', 'Form', 'optional', ALL_ROUTES, {
        fields: [
          { label: 'Client-facing email', map: '{{public_email}}', required: false },
          { label: 'First name', map: '{{public_first}}', required: false },
          { label: 'Last name', map: '{{public_last}}', required: false },
          { label: 'Phone number', map: '{{public_phone}}', required: false },
        ],
      }),
      step('Create Your Email Signature', 'Simple', 'optional', ['san', 'aa'], { video: true }),
      step('Understanding E&O Insurance', 'Simple', 'optional', ALL_ROUTES, { video: true }),
      step('Create Terms & Conditions', 'Form', 'optional', ALL_ROUTES, {
        video: true,
        fields: [
          { label: 'Terms & Conditions text', map: '{{terms_text}}', required: false },
          { label: 'Published terms URL', map: '{{terms_url}}', required: false },
        ],
      }),
      step('Create Your Service Level Agreement', 'Form', 'optional', ALL_ROUTES, {
        video: true,
        fields: [
          { label: 'Charging model', map: '{{charge_model}}', required: false },
          { label: 'Response time', map: '{{response_time}}', required: false },
          { label: 'Services included', map: '{{sla_services}}', required: false },
        ],
      }),
      step('Set Your SMART Goals', 'Form', 'optional', ALL_ROUTES, {
        video: true,
        fields: [
          { label: '90-day goal', map: '{{goal_90}}', required: false },
          { label: '6-month goal', map: '{{goal_180}}', required: false },
          { label: '1-year goal', map: '{{goal_365}}', required: false },
        ],
      }),
    ],
  },
  {
    id: 'sales-marketing',
    order: 4,
    name: 'Sales + Marketing Foundation',
    navLabel: 'Sales + Marketing',
    description: 'Find clients and start marketing.',
    routes: ALL_ROUTES,
    buildOut: true,
    steps: [
      step('Understanding Commissions', 'Simple', 'optional', ALL_ROUTES, { video: true }),
      step('Your Marketing Toolkit', 'Simple', 'optional', ALL_ROUTES, { video: true }),
      step('Draft Your Social Bio', 'Form — AI', 'optional', ALL_ROUTES, {
        seeds: ['Platform', 'Tone', 'Niche'],
        aiPrompt:
          'Write a short social bio for a travel advisor using {{niche}} and {{company_name}}, in the chosen tone.',
        aiVariables: ['{{niche}}', '{{company_name}}', '{{pathway}}'],
        aiOutput: '{{social_bio}}',
      }),
      step('Build Your First Campaign', 'Simple', 'optional', ALL_ROUTES, { video: true }),
    ],
  },
  {
    id: 'platforms-suppliers',
    order: 5,
    name: 'Platforms + Suppliers',
    navLabel: 'Platforms + Suppliers',
    description: 'Set up tools and supplier access.',
    routes: ALL_ROUTES,
    buildOut: true,
    steps: [
      step('Platform Navigation Tour', 'Simple', 'optional', ALL_ROUTES),
      step('Set Up Your CRM', 'Simple', 'optional', ALL_ROUTES, { video: true }),
      step('Sea + Sand Supplier Setup', 'Simple', 'optional', ALL_ROUTES, {
        pathways: ['Sea + Sand'],
      }),
      step('Register with Preferred Suppliers', 'Simple', 'optional', ALL_ROUTES),
    ],
  },
  {
    id: 'activation-growth',
    order: 6,
    name: 'Activation + Growth',
    navLabel: 'Activation + Growth',
    description: 'First booking and ongoing growth.',
    routes: ALL_ROUTES,
    buildOut: true,
    steps: [
      step('Book Your First Trip', 'Simple', 'optional', ALL_ROUTES, { video: true }),
      step('Join the Facebook Community', 'Simple', 'skippable', ALL_ROUTES),
      step('Explore Agent Universe', 'Simple', 'optional', ALL_ROUTES, { video: true }),
      step('Your 90-Day Growth Plan', 'Form', 'optional', ALL_ROUTES, {
        fields: [
          { label: 'Revenue target', map: '{{growth_target}}', required: false },
          { label: 'Focus pathway', map: '{{growth_focus}}', required: false },
        ],
      }),
    ],
  },
];

export const ACTIVATION_MODULES: ActivationModule[] = MODULE_SEEDS.map((seed) => ({
  ...seed,
  steps: seed.steps.map((s, index) => ({
    ...s,
    key: `${seed.id}:${index}`,
    moduleId: seed.id,
    moduleName: seed.name,
  })),
}));

export const findActivationStep = (
  modules: ActivationModule[],
  key: string | null,
): ActivationStep | null => {
  if (!key) return null;
  for (const module of modules) {
    const match = module.steps.find((s) => s.key === key);
    if (match) return match;
  }
  return null;
};

export const findActivationModule = (
  modules: ActivationModule[],
  id: string | null,
): ActivationModule | null => (id ? modules.find((m) => m.id === id) ?? null : null);

/* ── New-step examples ───────────────────────────────────────────────────── */

/**
 * The pre-filled step shown when an admin picks a type from the add-step menu.
 * Every field is populated so the shape of the type is obvious before saving.
 */
export const exampleStepForType = (type: StepTypeOption['type']): ActivationStep => {
  const base = {
    key: `new:${type}`,
    moduleId: 'activation',
    moduleName: 'Activation',
    routes: ALL_ROUTES,
    isExample: true,
  };

  switch (type) {
    case 'Simple':
      return {
        ...base,
        name: 'Watch & Learn (example Simple Step)',
        type: 'Simple',
        requirement: 'required',
        video: true,
        emails: [{ subject: 'New lesson available', to: 'Member', trigger: 'On publish' }],
      };
    case 'Form':
      return {
        ...base,
        name: 'Collect Info (example Form Answer)',
        type: 'Form',
        requirement: 'optional',
        fields: [
          { label: 'Business name', map: '{{company_name}}', required: true },
          { label: 'Website URL', map: '{{website}}', required: false },
          { label: 'Service area', map: '{{service_area}}', required: false },
        ],
      };
    case 'Form — AI':
      return {
        ...base,
        name: 'Draft with AIVIA (example AI Form)',
        type: 'Form — AI',
        requirement: 'required',
        seeds: ['Keywords', 'Draft text', 'Tone'],
        aiPrompt:
          "Polish the member's draft into a confident, client-facing statement using {{niche}}, {{pathway}}, and {{company_name}}. Keep it to two sentences.",
        aiVariables: ['{{niche}}', '{{pathway}}', '{{company_name}}', '{{experience_level}}'],
        aiOutput: '{{example_output}}',
      };
    case 'Add-On':
    default:
      return {
        ...base,
        name: 'Offer an Add-On (example Add-On)',
        type: 'Add-On',
        requirement: 'required',
        video: true,
        emails: [],
      };
  }
};

/** Profile fields offered by the searchable `{{variable}}` picker on AI steps. */
export const PROFILE_FIELD_VARIABLES = [
  '{{primary_goal}}',
  '{{travel_types}}',
  '{{experience_level}}',
  '{{sales_revenue}}',
  '{{weekly_hours}}',
  '{{pathway}}',
  '{{company_name}}',
  '{{niche}}',
  '{{tagline}}',
  '{{elevator_pitch}}',
  '{{value_prop}}',
  '{{public_email}}',
  '{{public_phone}}',
  '{{website}}',
  '{{service_area}}',
];

/* ── Content blocks (Simple steps) ───────────────────────────────────────── */

export type ContentBlockKind = 'video' | 'text' | 'file' | 'cta';

export interface ContentBlock {
  kind: ContentBlockKind;
  name: string;
  detail: string;
  /** Borrows the step-type palette so the tiles stay on one colour system. */
  type: ActivationStepType;
}

export const contentBlocksForStep = (s: ActivationStep): ContentBlock[] => {
  const blocks: ContentBlock[] = [];
  if (s.video) {
    blocks.push({ kind: 'video', name: 'Video', detail: 'Embedded — replace URL', type: 'Simple' });
  }
  blocks.push({
    kind: 'text',
    name: 'Text',
    detail: 'Rich text — bold, italic, links',
    type: 'System',
  });
  if (/W-9|EIN|Formation/.test(s.name)) {
    blocks.push({
      kind: 'file',
      name: 'File attachment',
      detail: 'Downloadable template',
      type: 'Form',
    });
  }
  blocks.push({ kind: 'cta', name: 'CTA button', detail: 'Label + URL', type: 'Add-On' });
  return blocks;
};

/* ── Conditional visibility rules ────────────────────────────────────────── */

export interface VisibilityRule {
  field: string;
  operator: string;
  value: string;
}

/** Plan tiers a step is offered to — derived from its route scope. */
export const plansForStep = (s: ActivationStep): ActivationPlan[] => {
  if (s.plans) return s.plans;
  const multi = s.routes.includes('mae') || s.routes.includes('aa');
  const single = s.routes.includes('san') || s.routes.includes('sae');
  if (multi && !single) return ['Team', 'Enterprise'];
  return [...ACTIVATION_PLANS];
};

export const visibilityRulesForStep = (s: ActivationStep): VisibilityRule[] => {
  const rules: VisibilityRule[] = [];
  if (s.routes.length < ALL_ROUTES.length) {
    rules.push({
      field: 'Route',
      operator: 'is one of',
      value: s.routes.map(routeShort).join(', '),
    });
  }
  if (s.pathways) {
    rules.push({ field: 'Pathway', operator: 'is', value: s.pathways.join(', ') });
  }
  if (s.plans) {
    rules.push({ field: 'Plan tier', operator: 'is', value: s.plans.join(', ') });
  }
  if (!rules.length) {
    rules.push({ field: 'Route', operator: 'is', value: 'All routes' });
  }
  return rules;
};

/** Fields the rule builder can branch on (per step or module). */
export const VISIBILITY_RULE_FIELDS = [
  'Route',
  'Plan tier',
  'Pathway',
  'Member answer',
  'Experience level',
];

/* ── Filters ─────────────────────────────────────────────────────────────── */

export type FilterGroupId =
  | 'stepType'
  | 'route'
  | 'pathway'
  | 'requirement'
  | 'module'
  | 'state';

export interface FilterOption {
  /** Stable value the filter state stores — never the display label. */
  id: string;
  label: string;
}

export interface FilterGroup {
  id: FilterGroupId;
  name: string;
  options: FilterOption[];
}

/**
 * Options are derived from the model (routes, pathways, modules) rather than
 * re-typed, so a filter can't silently drop a value, and every option carries a
 * stable id so renaming a label never changes what the filter matches.
 */
export const ACTIVATION_FILTER_GROUPS: FilterGroup[] = [
  {
    id: 'stepType',
    name: 'Step type',
    // `Form` covers both form types — the legend shows one Form colour.
    options: (['Add-On', 'Simple', 'Form', 'System'] as const).map((t) => ({ id: t, label: t })),
  },
  {
    id: 'route',
    name: 'Route',
    options: ACTIVATION_ROUTES.map((r) => ({ id: r.code, label: r.short })),
  },
  {
    id: 'pathway',
    name: 'Pathway',
    options: ACTIVATION_PATHWAYS.map((p) => ({ id: p, label: p })),
  },
  {
    id: 'requirement',
    name: 'Requirement',
    options: (['required', 'optional', 'skippable'] as const).map((r) => ({
      id: r,
      label: REQUIREMENT_LABELS[r],
    })),
  },
  {
    id: 'module',
    name: 'Module',
    options: ACTIVATION_MODULES.map((m) => ({ id: m.id, label: m.name })),
  },
  {
    id: 'state',
    name: 'State',
    options: [
      { id: 'active', label: 'Active' },
      { id: 'archived', label: 'Archived' },
    ],
  },
];

export const LEGEND_TYPES: ActivationStepType[] = ['Add-On', 'Simple', 'Form', 'System'];

/* ── Archive ─────────────────────────────────────────────────────────────── */

export interface ArchivedModuleRow {
  name: string;
  meta: string;
}

export interface ArchivedStepRow {
  name: string;
  type: ActivationStepType;
  meta: string;
}

export const ARCHIVED_MODULES: ArchivedModuleRow[] = [
  {
    name: 'Legacy Welcome (2024)',
    meta: 'Module · 4 steps · archived Mar 3, 2026 by Britta Roppe',
  },
];

export const ARCHIVED_STEPS: ArchivedStepRow[] = [
  {
    name: 'Intro Survey (v1)',
    type: 'Simple',
    meta: 'was Activation · archived Feb 12, 2026 — replaced by “Tell Us About Your Business”',
  },
  {
    name: 'Premium Coaching Upsell',
    type: 'Add-On',
    meta: 'was Business Foundation · archived Jan 28, 2026 — pricing under review',
  },
  {
    name: 'Old Facebook Group Invite',
    type: 'Simple',
    meta: 'was Activation + Growth · archived Dec 9, 2025 — superseded by community step',
  },
];

/* ── KPIs ────────────────────────────────────────────────────────────────── */

export interface KpiCard {
  label: string;
  value: string;
  sub: string;
  tone: 'neutral' | 'orange' | 'teal' | 'red';
}

export interface ModuleBar {
  name: string;
  count: number;
  completion: number;
  bar: string;
}

export interface TimeBar {
  name: string;
  days: number;
}

export interface SkipRow {
  name: string;
  count: number;
}

export interface StalledRow {
  member: string;
  plan: string;
  route: string;
  step: string;
  days: number;
  outreach: string;
}

export const KPI_CARDS: KpiCard[] = [
  { label: 'In activation', value: '428', sub: 'active members', tone: 'neutral' },
  { label: 'Avg. to activate', value: '6.4d', sub: 'checkout → active', tone: 'orange' },
  { label: 'Overall completion', value: '73%', sub: 'reach Activation', tone: 'teal' },
  { label: 'Stalled', value: '34', sub: 'inactive 7+ days', tone: 'red' },
];

export const KPI_MODULE_BARS: ModuleBar[] = [
  { name: 'Checkout', count: 48, completion: 98, bar: 'bg-violet-500' },
  { name: 'Activation', count: 96, completion: 91, bar: 'bg-orange-500' },
  { name: 'Business Foundation', count: 74, completion: 78, bar: 'bg-pink-600' },
  { name: 'Sales + Marketing', count: 52, completion: 64, bar: 'bg-teal-600' },
  { name: 'Platforms + Suppliers', count: 38, completion: 57, bar: 'bg-sky-500' },
  { name: 'Activation + Growth', count: 27, completion: 41, bar: 'bg-amber-500' },
];

export const KPI_TIME_BARS: TimeBar[] = [
  { name: 'Checkout', days: 0.4 },
  { name: 'Activation', days: 3.2 },
  { name: 'Business Foundation', days: 5.1 },
  { name: 'Sales + Marketing', days: 6.8 },
  { name: 'Platforms + Suppliers', days: 4.3 },
  { name: 'Activation + Growth', days: 9.5 },
];

export const KPI_SKIP_ROWS: SkipRow[] = [
  { name: 'Attend the Newbie Meet + Greet', count: 132 },
  { name: 'Banking & Direct Deposit', count: 86 },
  { name: 'Create Terms & Conditions', count: 74 },
  { name: 'Certificate of Formation', count: 61 },
  { name: 'Your Service Level Agreement', count: 53 },
];

export const KPI_STALLED_COUNT = 34;

export const KPI_STALLED_FILTERS = ['Step', 'Plan', 'Role', 'Experience'];

export const KPI_STALLED_ROWS: StalledRow[] = [
  { member: 'Marcus Lee', plan: 'Growth', route: 'N1', step: 'Background Check', days: 9, outreach: 'Sent 1/2' },
  { member: 'Dana Whitfield', plan: 'Team', route: 'EM', step: 'Upload EIN Letter', days: 12, outreach: 'Sent 2/2' },
  { member: 'Priya Nadar', plan: 'Individual', route: 'E1', step: 'Verify Identity', days: 8, outreach: 'Sent 1/2' },
  { member: 'Tom Alvarez', plan: 'Team', route: 'N1', step: 'Define Your Niche', days: 15, outreach: 'Team follow-up' },
  { member: 'Grace Kim', plan: 'Enterprise', route: 'EM', step: 'Banking & Direct Deposit', days: 7, outreach: 'Not started' },
  { member: 'Owen Brooks', plan: 'Growth', route: 'N1', step: 'Performance Guaranty', days: 11, outreach: 'Sent 2/2' },
];

export const OUTREACH_TONE_CLASSES: Record<string, string> = {
  'Sent 1/2': 'text-amber-700 dark:text-amber-400',
  'Sent 2/2': 'text-red-700 dark:text-red-400',
  'Team follow-up': 'text-violet-700 dark:text-violet-400',
  'Not started': 'text-gray-500 dark:text-gray-400',
};
